import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  CircularProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, CloudUpload as CloudUploadIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';
import { adminHasPermission } from '../../../utils/adminPermissions';
import ReactQuill from 'react-quill-new';
import { QUILL_MODULES, QUILL_FORMATS, plainTextLength } from '../../../utils/quillQuestionEditorConfig';

const FlashCardForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);
  const canSave = isEdit
    ? adminHasPermission('update-flash-cards')
    : adminHasPermission('create-flash-cards');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [contentJson, setContentJson] = useState('{}');
  const [subjectId, setSubjectId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [subTopicId, setSubTopicId] = useState('');
  const [schoolYearId, setSchoolYearId] = useState('');
  const [status, setStatus] = useState(2);
  const [difficulty, setDifficulty] = useState('normal');
  const [sortOrder, setSortOrder] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    api.get('admin/flash-cards/subjects')
      .then((r) => { if (r.data?.success && r.data?.data) setSubjects(r.data.data); })
      .catch(() => {});
    api.get('admin/flash-cards/school-years')
      .then((r) => { if (r.data?.success && r.data?.data) setSchoolYears(r.data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!subjectId) {
      setTopics([]);
      setTopicId('');
      setSubtopics([]);
      setSubTopicId('');
      return;
    }
    api.get('admin/flash-cards/topics', { params: { subject_id: subjectId } })
      .then((r) => {
        if (r.data?.success && r.data?.data) setTopics(r.data.data);
        else setTopics([]);
      })
      .catch(() => setTopics([]));
    setTopicId('');
    setSubtopics([]);
    setSubTopicId('');
  }, [subjectId]);

  useEffect(() => {
    if (!topicId) {
      setSubtopics([]);
      setSubTopicId('');
      return;
    }
    api.get('admin/flash-cards/subtopics', { params: { topic_id: topicId } })
      .then((r) => {
        if (r.data?.success && r.data?.data) setSubtopics(r.data.data);
        else setSubtopics([]);
      })
      .catch(() => setSubtopics([]));
    setSubTopicId('');
  }, [topicId]);

  useEffect(() => {
    if (!isEdit) return;
    setFetching(true);
    api.get(`admin/flash-cards/${id}`)
      .then((r) => {
        if (r.data?.success && r.data?.data) {
          const d = r.data.data;
          setTitle(d.title || '');
          setDescription(d.description || '');
          setBody(typeof d.content === 'object' && d.content?.body != null ? d.content.body : '');
          setContentJson(d.content ? JSON.stringify(d.content, null, 2) : '{}');
          setSubjectId(d.subject_id ?? '');
          setTopicId(d.topic_id ?? '');
          setSubTopicId(d.sub_topic_id ?? '');
          setSchoolYearId(d.school_year_id ?? '');
          setStatus(d.status ?? 2);
          setDifficulty(d.difficulty === 'medium' ? 'normal' : (d.difficulty || 'normal'));
          setSortOrder(d.sort_order ?? 0);
          setImageUrl(d.image_url || null);
        }
      })
      .catch(() => toast.error('Failed to load flash card'))
      .finally(() => setFetching(false));
  }, [id, isEdit]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPG, PNG, etc.)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2 MB');
      return;
    }
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) {
      toast.error('You do not have permission to save flash cards.');
      return;
    }
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    let content = null;
    if (showAdvanced && contentJson.trim()) {
      try {
        content = JSON.parse(contentJson);
      } catch {
        toast.error('Advanced content must be valid JSON');
        return;
      }
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', plainTextLength(description) === 0 ? '' : String(description).trim());
      formData.append('body', plainTextLength(body) === 0 ? '' : String(body).trim());
      if (content) formData.append('content', JSON.stringify(content));
      formData.append('subject_id', subjectId || '');
      formData.append('topic_id', topicId || '');
      formData.append('sub_topic_id', subTopicId || '');
      formData.append('school_year_id', schoolYearId || '');
      formData.append('status', Number(status));
      formData.append('difficulty', difficulty || 'normal');
      formData.append('sort_order', Number(sortOrder) || 0);
      if (imageFile) formData.append('image', imageFile);
      if (removeImage) formData.append('remove_image', '1');

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (isEdit) {
        await api.put(`admin/flash-cards/${id}`, formData, config);
        toast.success('Flash card updated');
      } else {
        await api.post('admin/flash-cards', formData, config);
        toast.success('Flash card created');
      }
      navigate('/admin/flash-cards');
    } catch (err) {
      const msg = err.response?.data?.message || (err.response?.data?.errors ? Object.values(err.response.data.errors).flat().join(' ') : 'Failed to save');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/flash-cards')}>
          Back
        </Button>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 0.5 }}>
          {isEdit ? 'Edit Flash Card' : 'Add Flash Card'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Description
              </Typography>
              <Box
                sx={{
                  '& .quill': { background: '#fff', borderRadius: 1 },
                  '& .ql-container': {
                    minHeight: '120px',
                    fontSize: '14px',
                    borderRadius: '0 0 4px 4px',
                  },
                  '& .ql-toolbar': {
                    borderRadius: '4px 4px 0 0',
                    background: '#f9f9f9',
                    flexWrap: 'wrap',
                  },
                  '& .ql-editor': { minHeight: '120px' },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={description}
                  onChange={setDescription}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Short description (formatting, lists, images…)"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={subjectId}
                  onChange={(e) => setSubjectId(e.target.value)}
                  label="Subject"
                >
                  <MenuItem value="">None</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!subjectId}>
                <InputLabel>Topic</InputLabel>
                <Select
                  value={topicId}
                  onChange={(e) => setTopicId(e.target.value)}
                  label="Topic"
                >
                  <MenuItem value="">None</MenuItem>
                  {topics.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!topicId}>
                <InputLabel>Sub topic</InputLabel>
                <Select
                  value={subTopicId}
                  onChange={(e) => setSubTopicId(e.target.value)}
                  label="Sub topic"
                >
                  <MenuItem value="">None</MenuItem>
                  {subtopics.map((st) => (
                    <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>School year</InputLabel>
                <Select
                  value={schoolYearId}
                  onChange={(e) => setSchoolYearId(e.target.value)}
                  label="School year"
                >
                  <MenuItem value="">None</MenuItem>
                  {schoolYears.map((sy) => (
                    <MenuItem key={sy.id} value={sy.id}>{sy.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} label="Difficulty">
                  <MenuItem value="easy">Easy</MenuItem>
                  <MenuItem value="normal">Medium</MenuItem>
                  <MenuItem value="hard">Hard</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(Number(e.target.value))}
                  label="Status"
                >
                  <MenuItem value={1}>Inactive</MenuItem>
                  <MenuItem value={2}>Active</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Card image (optional)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose image
                </Button>
                {(imagePreview || (imageUrl && !removeImage)) && (
                  <>
                    <Box component="img" src={imagePreview || imageUrl} alt="Preview" sx={{ maxHeight: 120, maxWidth: 200, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1 }} />
                    <IconButton size="small" color="error" onClick={handleRemoveImage} title="Remove image">
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>JPG, PNG or GIF. Max 2 MB.</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Content (optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Shown on the back when students flip the card (if set; otherwise the description is used).
              </Typography>
              <Box
                sx={{
                  '& .quill': { background: '#fff', borderRadius: 1 },
                  '& .ql-container': {
                    minHeight: '180px',
                    fontSize: '14px',
                    borderRadius: '0 0 4px 4px',
                  },
                  '& .ql-toolbar': {
                    borderRadius: '4px 4px 0 0',
                    background: '#f9f9f9',
                    flexWrap: 'wrap',
                  },
                  '& .ql-editor': { minHeight: '180px' },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={body}
                  onChange={setBody}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Back-of-card content (formatting, lists, images…)"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button
                size="small"
                startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowAdvanced((v) => !v)}
                sx={{ mb: 1 }}
              >
                {showAdvanced ? 'Hide advanced (JSON)' : 'Advanced: edit as JSON'}
              </Button>
              <Collapse in={showAdvanced}>
                <TextField
                  fullWidth
                  label="Content (JSON)"
                  value={contentJson}
                  onChange={(e) => setContentJson(e.target.value)}
                  multiline
                  rows={6}
                  placeholder='{"cardType": "place-value", "placeValue": {...}}'
                  helperText="For advanced users only. Valid JSON for card-specific data."
                />
              </Collapse>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={loading || !canSave} sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5a6fd6' } }}>
                {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default FlashCardForm;
